/**
 * функция для форматирования денежной суммы в валюте
 * вида 00 000,00 ₽
 *
 * @param {Number|String} value - денежная сумма для форматирования
 * @param {'decimal'|'currency'} style - decimal - отображение без знака валюты,
 * по умолчанию знак отображается
 * @returns {String} возвращает отформатированную строку 00 000,00 ₽
 */

export type Style = 'currency' | 'decimal';

function isValidCurrencyCode(code?: string): boolean {
  if (!code || code.length !== 3) return false;

  try {
    new Intl.NumberFormat('en-US', { style: 'currency', currency: code });

    return true;
  } catch {
    return false;
  }
}

export default function formatToCurrency(value: string | number, style: Style = 'currency', currencyPrecision = 2, currency?: string) {
  let fraction = 2;

  if (typeof currencyPrecision === 'number' && !isNaN(currencyPrecision) && Number.isInteger(currencyPrecision)) {
    fraction = currencyPrecision;
  }

  const resolvedStyle = style === 'currency' && !isValidCurrencyCode(currency) ? 'decimal' : style;

  const options: Intl.NumberFormatOptions = {
    minimumFractionDigits: fraction,
    style: resolvedStyle,
    ...(resolvedStyle === 'currency' && currency ? { currency } : {}),
  };

  const numberFormat = new Intl.NumberFormat('en-US', options);

  if (resolvedStyle === 'currency') {
    const parts = numberFormat.formatToParts(Number(value));
    const symbol = parts.find(p => p.type === 'currency')?.value ?? '';
    const number = parts.filter(p => p.type !== 'currency').map(p => p.value)
      .join('')
      .trim();

    return `${number} ${symbol}`;
  }

  return numberFormat.format(Number(value));
}
